import {
  Add,
  Delete,
  Edit,
  Print as PrintIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import PrinterService from '../../services/printer';

const PRINTER_TYPES = [
  { value: 'network', label: 'Red (Ethernet/WiFi)' },
  { value: 'usb', label: 'USB' },
  { value: 'pdf', label: 'PDF' },
];

const emptyForm = {
  _id: null,
  enabled: true,
  printerType: 'network',
  printerName: '',
  ip: '',
  port: 9100,
  usbPrinterName: '',
  paperWidth: 80,
  autoCut: true,
  pdfOutputPath: '',
};

const typeLabel = (type) => PRINTER_TYPES.find((t) => t.value === type)?.label || type;

function PrinterSettingsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [openForm, setOpenForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [snack, setSnack] = useState({ open: false, severity: 'success', message: '' });

  const showSnack = (severity, message) => setSnack({ open: true, severity, message });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await PrinterService.getPrinterSettings();
      setRows(Array.isArray(data) ? data : []);
    } catch {
      showSnack('error', 'No se pudieron cargar las impresoras.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    const fetchInitial = async () => {
      setLoading(true);
      try {
        const data = await PrinterService.getPrinterSettings(controller.signal);
        setRows(Array.isArray(data) ? data : []);
      } catch (err) {
        if (err?.name === 'CanceledError' || err?.name === 'AbortError') return;
        showSnack('error', 'No se pudieron cargar las impresoras.');
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    fetchInitial();
    return () => controller.abort();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const resetForm = () => {
    setForm(emptyForm);
    setErrors({});
  };

  const handleOpenCreate = () => {
    resetForm();
    setOpenForm(true);
  };

  const handleOpenEdit = (row) => {
    setErrors({});
    setForm({
      _id: row._id,
      enabled: !!row.enabled,
      printerType: row.printerType || 'network',
      printerName: row.printerName || '',
      ip: row.ip || '',
      port: row.port ?? 9100,
      usbPrinterName: row.usbPrinterName || '',
      paperWidth: row.paperWidth ?? 80,
      autoCut: row.autoCut !== false,
      pdfOutputPath: row.pdfOutputPath || '',
    });
    setOpenForm(true);
  };

  const validate = () => {
    const e = {};
    if (!form.printerName?.trim()) e.printerName = 'El nombre es requerido.';
    if (form.printerType === 'network') {
      if (!form.ip?.trim()) {
        e.ip = 'La IP es requerida.';
      } else if (!/^(\d{1,3}\.){3}\d{1,3}$/.test(form.ip.trim()) && !/^[a-zA-Z0-9]([a-zA-Z0-9\-._]*)$/.test(form.ip.trim())) {
        e.ip = 'Formato de IP o hostname no válido.';
      }
      if (!form.port) {
        e.port = 'El puerto es requerido.';
      } else if (Number(form.port) < 1 || Number(form.port) > 65535) {
        e.port = 'El puerto debe estar entre 1 y 65535.';
      }
    }
    if (form.printerType === 'usb') {
      if (!form.usbPrinterName?.trim()) e.usbPrinterName = 'El nombre del dispositivo USB es requerido.';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const buildPayload = () => ({
    enabled: form.enabled,
    printerType: form.printerType,
    printerName: form.printerName.trim(),
    ip: form.ip?.trim() || '',
    port: Number(form.port) || 9100,
    usbPrinterName: form.usbPrinterName?.trim() || '',
    paperWidth: Number(form.paperWidth) || 80,
    autoCut: !!form.autoCut,
    pdfOutputPath: form.pdfOutputPath?.trim() || '',
  });

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = buildPayload();
      if (form._id) {
        await PrinterService.updatePrinterSettings(form._id, payload);
        showSnack('success', 'Impresora actualizada.');
      } else {
        await PrinterService.createPrinterSettings(payload);
        showSnack('success', 'Impresora creada.');
      }
      setOpenForm(false);
      resetForm();
      loadData();
    } catch {
      showSnack('error', 'No se pudo guardar. Revisá los datos e intentá de nuevo.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!toDelete?._id) return;
    setDeleting(true);
    try {
      await PrinterService.deletePrinterSettings(toDelete._id);
      showSnack('success', 'Impresora eliminada.');
      setToDelete(null);
      loadData();
    } catch {
      showSnack('error', 'No se pudo eliminar.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: 'linear-gradient(120deg,#fafafa 0%,#f5f7fa 35%,#eef3f9 100%)',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          background: 'rgba(255,255,255,0.9)',
          backdropFilter: 'blur(6px)',
          px: 3,
          py: 2,
          zIndex: 2,
          borderBottom: '1px solid rgba(0,0,0,0.06)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <PrintIcon color="primary" />
          <Typography variant="h6" fontWeight={600}>
            Configuración de Impresoras
          </Typography>
        </Stack>
        <Button startIcon={<Add />} variant="contained" onClick={handleOpenCreate}>
          Agregar impresora
        </Button>
      </Box>

      {loading && <LinearProgress />}

      {/* Table */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ background: '#f5f7fa' }}>
                <TableCell sx={{ fontWeight: 600 }}>Nombre</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Tipo</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Estado</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Conexión</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Papel</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">
                  Acciones
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row._id} hover>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <PrintIcon fontSize="small" color="action" />
                      <Typography variant="body2" fontWeight={500}>
                        {row.printerName}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{typeLabel(row.printerType)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={row.enabled ? 'Activa' : 'Inactiva'}
                      color={row.enabled ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {row.printerType === 'network'
                        ? `${row.ip}:${row.port}`
                        : row.printerType === 'usb'
                        ? row.usbPrinterName
                        : row.pdfOutputPath || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {row.paperWidth}mm
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton onClick={() => handleOpenEdit(row)} size="small" title="Editar">
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton
                      onClick={() => setToDelete(row)}
                      size="small"
                      color="error"
                      title="Eliminar"
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}

              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                    <Stack spacing={1.5} alignItems="center">
                      <PrintIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
                      <Typography variant="body2" color="text.secondary">
                        No hay impresoras configuradas.
                      </Typography>
                      <Button
                        startIcon={<Add />}
                        variant="outlined"
                        size="small"
                        onClick={handleOpenCreate}
                      >
                        Agregar la primera impresora
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      </Box>

      {/* Create / Edit dialog */}
      <Dialog
        open={openForm}
        onClose={() => !saving && setOpenForm(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{form._id ? 'Editar impresora' : 'Nueva impresora'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={0.5}>
            <TextField
              label="Nombre de la impresora"
              value={form.printerName}
              onChange={(e) => setForm((p) => ({ ...p, printerName: e.target.value }))}
              required
              error={!!errors.printerName}
              helperText={errors.printerName}
              fullWidth
              autoFocus
            />

            <TextField
              select
              label="Tipo de impresora"
              value={form.printerType}
              onChange={(e) => setForm((p) => ({ ...p, printerType: e.target.value }))}
              fullWidth
            >
              {PRINTER_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </TextField>

            <FormControlLabel
              control={
                <Switch
                  checked={form.enabled}
                  onChange={(e) => setForm((p) => ({ ...p, enabled: e.target.checked }))}
                />
              }
              label={form.enabled ? 'Activa' : 'Inactiva'}
            />

            {form.printerType === 'network' && (
              <>
                <TextField
                  label="Dirección IP / Hostname"
                  value={form.ip}
                  onChange={(e) => setForm((p) => ({ ...p, ip: e.target.value }))}
                  error={!!errors.ip}
                  helperText={errors.ip}
                  fullWidth
                  placeholder="192.168.1.100"
                />
                <TextField
                  type="number"
                  label="Puerto"
                  value={form.port}
                  onChange={(e) => setForm((p) => ({ ...p, port: e.target.value }))}
                  error={!!errors.port}
                  helperText={errors.port}
                  inputProps={{ min: 1, max: 65535 }}
                  fullWidth
                />
              </>
            )}

            {form.printerType === 'usb' && (
              <TextField
                label="Nombre del dispositivo USB"
                value={form.usbPrinterName}
                onChange={(e) => setForm((p) => ({ ...p, usbPrinterName: e.target.value }))}
                error={!!errors.usbPrinterName}
                helperText={errors.usbPrinterName}
                fullWidth
                placeholder="EPSON_TM_T20"
              />
            )}

            {form.printerType === 'pdf' && (
              <TextField
                label="Ruta de salida PDF"
                value={form.pdfOutputPath}
                onChange={(e) => setForm((p) => ({ ...p, pdfOutputPath: e.target.value }))}
                fullWidth
                placeholder="/tmp/tickets/"
              />
            )}

            <TextField
              type="number"
              label="Ancho del papel (mm)"
              value={form.paperWidth}
              onChange={(e) => setForm((p) => ({ ...p, paperWidth: e.target.value }))}
              inputProps={{ min: 40, max: 120 }}
              fullWidth
            />

            <FormControlLabel
              control={
                <Switch
                  checked={form.autoCut}
                  onChange={(e) => setForm((p) => ({ ...p, autoCut: e.target.checked }))}
                />
              }
              label="Corte automático"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenForm(false); resetForm(); }} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? 'Guardando...' : form._id ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!toDelete} onClose={() => !deleting && setToDelete(null)} maxWidth="xs">
        <DialogTitle>Eliminar impresora</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Estás seguro que querés eliminar{' '}
            <strong>{toDelete?.printerName}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToDelete(null)} disabled={deleting}>
            Cancelar
          </Button>
          <Button onClick={handleDelete} variant="contained" color="error" disabled={deleting}>
            {deleting ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snack.severity}
          variant="filled"
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default PrinterSettingsPage;
